import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { SessionStorageService } from './session-storage.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** Friendly screen name from a route path, e.g. "/purchase/purchase-order" -> "Purchase Order". */
function screenNameFromPath(path: string): string {
  const seg = path.split('/').filter(Boolean).pop() || 'home';
  return seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const sessionStorage = inject(SessionStorageService);
  const router = inject(Router);

  const token = sessionStorage.getToken() ?? '';
  // Force toString() to avoid HttpHeaders crash when values are numbers
  const companyId = String(sessionStorage.getItem<any>('companyId') ?? '');
  const groupId = String(sessionStorage.getItem<any>('groupId') ?? '');
  const username = String(sessionStorage.getItem<string>('username') ?? 'System');

  // Current screen the request came from — the route path is a stable, unique
  // per-screen id the backend audit log records (X-Screen-Id / X-Screen-Name).
  const screenId = (router.url || '/').split('?')[0];
  const screenName = screenNameFromPath(screenId);

  // 🟩 Skip interceptor only for login — NOT for permissions/menu
  if (req.url.includes('/sign-in')) {
    return next(req);
  }

  // ✅ Clone the request and keep existing headers
  const authReq = req.clone({
    setHeaders: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(companyId && companyId !== 'null' && { 'X-CompanyId': companyId }),
      ...(groupId && groupId !== 'null' && { 'X-GroupId': groupId }),
      ...(username && username !== 'null' && { 'X-User-Name': username }),
      'X-Screen-Id': screenId,
      'X-Screen-Name': screenName
    }
  });

  // ✅ Global unauthorized handler
  return next(authReq).pipe(
    catchError((error) => {
      if (error.status === 401) {
        sessionStorage.clear();
        router.navigate(['/sign-in']);
      }
      return throwError(() => error);
    })
  );
};
